import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Table, Column, AutoSizer } from 'react-virtualized';
import { Button, Col, Row, Spinner } from 'reactstrap';
import { Chart as ChartJS, registerables } from 'chart.js';
import Title from '../../../sharedComponents/Title/Title';
import useDataSuite from '../useDataSuite';
import { tableColors, Theme } from '../../../config/constants';
import { FaClipboardList, FaTrash } from 'react-icons/fa';
import { BsPencilSquare, BsCircleFill } from 'react-icons/bs';
import formatters from '../../../config/formatters';
import { IoMdInformationCircle } from "react-icons/io";
import { Tooltip } from "@mui/material";
import { Bar } from 'react-chartjs-2';
import useDataSuiteWidgets from './useDataSuiteWidgets';
import EmptyDataMessage from '../../../sharedComponents/ErrorMessages/EmptyDataMessage';

ChartJS.register(...registerables);

const SalesByOperatorWidget = (props) => {
    const { w, i } = props;
    const {  onClickWidgetDrawerOpen, onClickDeleteWidget } = useDataSuite.useWidgetControls();
    const { widgetTitle, salesByOperatorBarData, salesByOperatorTableData, salesByOperatorTableLabels, tableOptions } = useDataSuiteWidgets.useSalesByOperatorWidget();
    const { onClickViewDetails } = useDataSuite.useDataSuite();
    const widgetOptionsOpen = useSelector(({ui}) => ui.slideDrawer);
    const isLoading = useSelector(({datasuite}) => datasuite.isLoading.salesByOperatorLoading);
    const { numberFormatter } = formatters();
    const [isWidgetHovered, setIsWidgetHovered] = useState(false);

    const handleMouseEnter = () => {
        setIsWidgetHovered(true);
    };
    const handleMouseLeave = () => {
        setIsWidgetHovered(false);
    };

    return (
        <Col onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{ position: 'relative'}} >
            <Row className='d-flex align-items-center'>
                <Col className='w-fc pr-0'>
                    <div className='d-flex align-items-center px-0'>
                    <Title title={widgetTitle}/>
                    <Tooltip title='Displays your sales by operator or chain. Helpful for identifying operator purchasing your products without a direct agreement, as well as confirming up-to-date volume and pricing information.' arrow>
                        <div className='pt-3 pb-2'>
                        <IoMdInformationCircle className="text-size-18 " style={{color: Theme().colors.accent}}/>
                        </div>
                    </Tooltip>
                    </div>
                </Col>
                {(widgetOptionsOpen || isWidgetHovered)  && ( <>
                    <Col className='w-fc px-1 pt-3 pb-2'>
                        <Button
                            block
                            className='py-1 button-label replaceWidgetButton'
                            size='sm'
                            color='blueFilter'
                            onClick={() => onClickWidgetDrawerOpen("Replace Widget", true, i)}>
                            <BsPencilSquare color='white' />
                        </Button>
                    </Col>
                    <Col className='w-fc px-1 pt-3 pb-2'>
                        <Button
                            block
                            className='py-1 button-label deleteWidgetButton'
                            size='sm'
                            color='redLight'
                            onClick={() => onClickDeleteWidget(w)}>
                            <FaTrash color='white' />
                        </Button>
                    </Col>
                </>
                )}
                <Col className={`w-fc me-3 ms-auto px-1 pt-3 pb-2`}>
                <Button
                    block
                    className={`py-1 button-label expandWidgetButton`}
                    size='sm'
                    outline={true}
                    color={Theme().colors.accentName}
                    onClick = {() => onClickViewDetails(widgetTitle, props.prechosenFilters)}>
                    <FaClipboardList color={Theme().colors.accentName} style={{position: 'relative', bottom: '1px'}}/> View Details
                </Button>
                </Col>
            </Row>
            {isLoading &&
              <Row className="mx-auto" style={{minHeight: '315px'}}>
                  <Col className="mx-auto my-auto" style={{width:"2.5%"}}>
                      <Spinner style={{marginLeft:'50%'}} color='beplBlue' animation="border" />
                  </Col>
              </Row>
            }
            {(salesByOperatorTableData?.length <= 0) && !isLoading &&
            <Row className="mx-auto" style={{minHeight: '315px'}}>
                <Col className="mx-auto my-auto" style={{width:"2.5%"}}>
                    <EmptyDataMessage />
                </Col>
            </Row>
            }
            {(salesByOperatorTableData?.length > 0) && !isLoading &&
            <Row className="w-100 mb-3 mx-0" style={{minHeight: '315px', height: '315px'}}>
                <Col className='my-auto' style={{height: '100%', width: '40%', maxWidth: '40%', flex: '0 0 40%'}}>
                    <div style={{height: '100%'}}>
                        <Bar
                            data = {{
                                labels: salesByOperatorTableLabels,
                                datasets: [
                                    {
                                        label: '',
                                        data: salesByOperatorBarData,
                                        backgroundColor: tableColors
                                    }
                                ]
                            }}
                            options = {{ ...tableOptions, maintainAspectRatio: false }}
                        />
                    </div>
                </Col>
                <Col style={{width: '60%', maxWidth: '60%', flex: '0 0 60%'}}>
                    <Row style={{height: '100%'}}>
                        <AutoSizer>
                            {({width, height}) => (
                                <Table className="table salesByOperatorTable border-grayLight" id="salesByOperatorTable"
                                    width={width * 0.97}
                                    height={height * 0.95}
                                    headerHeight={34.5}
                                    rowHeight={38}
                                    rowCount={salesByOperatorTableData.length}
                                    rowGetter={({ index }) => salesByOperatorTableData[index]}
                                >
                                    <Column
                                    label='Location Name'
                                    dataKey='location_name'
                                    width={width * 0.5}
                                    cellRenderer={({cellData, rowIndex}) => (<span><BsCircleFill color={tableColors[rowIndex % 12]} /> {cellData}</span>)}
                                    />
                                    <Column
                                    width={width * 0.3}
                                    label='Category'
                                    dataKey='category'
                                    />
                                    <Column
                                    width={width * 0.2}
                                    label='Total Cases'
                                    dataKey='total_cases'
                                    cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                    />
                                </Table>
                            )}
                        </AutoSizer>
                    </Row>
                </Col>
            </Row>
            }
        </Col>
    );

}

export default SalesByOperatorWidget;
